import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';

import { TranslatePipe } from '@recruitee/i18n';
import type { OfferPromoteJobBoardsHotspot } from '@recruitee/integrations-types';
import type { DialogComponentI } from '@recruitee/user-interface';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  RtStatefulComponent,
} from '@recruitee/user-interface';

import { CurrentHotspotToken } from '../../../client/integrations-client.module';
import { Destroyable, takeUntilDestroyed } from '../../../common';
import { IntegrationEventActions } from '../../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { HotelprofessionalsApiService } from '../hotelprofessionals-api';
import {
  HotelprofessionalsPostingFormComponent,
  initializeEmptyPostingFormGroup,
} from '../hotelprofessionals-posting-form/hotelprofessionals-posting-form.component';
import type { CreatePostingForm, Offer, Posting } from '../types';

type State = {
  creating: boolean;
  errors: string[];
  errorFields: { [field: string]: string[] };
};

type DialogData = {
  offer: Offer;
};

@Component({
  selector: 'rti-hotelprofessionals-create-posting-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './hotelprofessionals-create-posting-dialog.component.html',
  styleUrls: ['./hotelprofessionals-create-posting-dialog.component.less'],
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ErrorMessagesWrapperComponent,
    HotelprofessionalsPostingFormComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class HotelprofessionalsCreatePostingDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<DialogData, boolean>
{
  public postingForm: UntypedFormGroup;

  constructor(
    public dialog: DialogRef,
    cdRef: ChangeDetectorRef,
    private hotelprofessionalsApi: HotelprofessionalsApiService,
    private store: Store,
    @Inject(CurrentHotspotToken)
    private currentHotspot: OfferPromoteJobBoardsHotspot,
  ) {
    super(cdRef);

    this.createState({
      creating: false,
      errors: [],
      errorFields: {},
    });
  }

  public setDialogData(data: DialogData): void {
    this.postingForm = initializeEmptyPostingFormGroup(data.offer);

    this.postingForm.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public publish(): void {
    this.setState({ ...this.state, creating: true });

    this.hotelprofessionalsApi.post(`posting`, this.createPostingForm).subscribe(
      (_data: { posting: Posting }) => {
        this.setState({ ...this.state, creating: false });

        this.store.dispatch(
          IntegrationEventActions.HotelprofessionalsPostingCreated({
            payload: { context: { hotspot: this.currentHotspot } },
          }),
        );

        this.dialog.close({ success: true });
      },
      ({ error }) => {
        this.setState({ ...this.state, creating: false, errors: error.error });
      },
    );
  }

  public close(): void {
    this.dialog.close();
  }

  public get formValid(): boolean {
    return this.postingForm.valid;
  }

  private get createPostingForm(): CreatePostingForm {
    return {
      offerId: this.currentHotspot.params.offerId,
      locationId: this.postingForm.value.locationId,
      departments: this.postingForm.value.departments,
      functions: this.postingForm.value.functions,
      employmentType: this.postingForm.value.employmentType,
      weeklyHoursMin: this.postingForm.value.weeklyHoursMin,
      weeklyHoursMax: this.postingForm.value.weeklyHoursMax,
      feature: this.postingForm.value.feature,
      salary: this.postingForm.value.salary,
      education: this.postingForm.value.education,
      experience: this.postingForm.value.experience,
      requiredLanguages: this.postingForm.value.requiredLanguages,
    };
  }
}
